import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, Search, Filter, ArrowRight } from "lucide-react";
import QstLayout from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import { qstLessons, qstTrades } from "./api";

const CATEGORY_LABEL = {
  "quality-control": "Quality Control",
  "safety":          "Safety",
  "loss-prevention": "Loss Prevention",
};

export default function QstLibraryPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [trade, setTrade] = useState(params.get("trade") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [freeOnly, setFreeOnly] = useState(params.get("free_only") === "1");
  const [tradesList, setTradesList] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Training Library — QST";
    qstTrades().then((d) => setTradesList(d.live || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setBusy(true);
    qstLessons({ q, trade, category, free_only: freeOnly })
      .then((r) => setItems(r.items || []))
      .finally(() => setBusy(false));
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (trade) p.set("trade", trade);
    if (category) p.set("category", category);
    if (freeOnly) p.set("free_only", "1");
    setParams(p, { replace: true });
  }, [q, trade, category, freeOnly]); // eslint-disable-line

  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "library" }} />
      <section className="bg-slate-50 border-b border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Training Library</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black text-[#0B2545]">Find the lesson your crew needs today.</h1>
          <div className="mt-6 flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-4 py-2.5 max-w-3xl">
            <Search className="w-4 h-4 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by topic, activity, or keyword…"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="qst-library-search" />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select value={trade} onChange={(e) => setTrade(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium"
              data-testid="qst-library-trade-filter">
              <option value="">All trades</option>
              {tradesList.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium"
              data-testid="qst-library-category-filter">
              <option value="">All topics</option>
              <option value="quality-control">Quality Control</option>
              <option value="safety">Safety</option>
              <option value="loss-prevention">Loss Prevention</option>
            </select>
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
              <input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)}
                data-testid="qst-library-free-toggle" />
              Free samples only
            </label>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10" data-testid="qst-library-results">
        <div className="text-sm text-slate-600 mb-4">
          {busy ? "Searching…" : `${items.length} lesson${items.length === 1 ? "" : "s"} found`}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((l) => (
            <Link key={l.slug} to={`/qst/lessons/${l.slug}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md transition"
              data-testid={`qst-library-lesson-${l.slug}`}>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                <span className={`rounded-md px-1.5 py-0.5 ${l.category === "safety" ? "bg-red-50 text-red-700" : l.category === "loss-prevention" ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>
                  {CATEGORY_LABEL[l.category]}
                </span>
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Clock className="w-3 h-3" /> {l.estimated_minutes} min
                </span>
                {l.free_sample && <span className="rounded-md bg-emerald-100 text-emerald-800 px-1.5 py-0.5">FREE</span>}
              </div>
              <div className="mt-3 text-base font-bold text-[#0B2545] leading-snug">{l.title}</div>
              <div className="mt-1.5 text-xs text-slate-600 line-clamp-2 leading-relaxed">{l.objective}</div>
              <div className="mt-3 text-[11px] text-slate-500">
                v{l.version} · reviewed {l.last_reviewed} · by {l.author}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#F97316] group-hover:translate-x-0.5 transition">
                Open <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </QstLayout>
  );
}
